import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DraftCardShell, { outlineButtonSx, primaryButtonSx, textButtonSx } from "../drafts/DraftCardShell";
import { questionProblems } from "../drafts/quizDraftOps";
import { AI_ASSISTANT_BLUE } from "../constants";

function QuestionEditor({ question, onSave, onCancel, disabled }) {
  const [text, setText] = useState(question.question_content || question.title || "");
  const [difficulty, setDifficulty] = useState(question.difficulty || "medium");
  const [explanation, setExplanation] = useState(question.explanation || "");
  const [answers, setAnswers] = useState(() => {
    const list = (question.answers || []).slice(0, 4).map((a) => ({ ...a }));
    while (list.length < 4) list.push({ answer_text: "", is_correct: false });
    return list;
  });
  const draft = { question_content: text, answers };
  const problems = questionProblems(draft);

  return (
    <Stack spacing={0.9} sx={{ mt: 1 }}>
      <TextField size="small" label="Question" value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} fullWidth multiline minRows={2} />
      {answers.map((a, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Stack key={i} direction="row" spacing={0.5} alignItems="center">
          <Radio
            size="small"
            checked={!!a.is_correct}
            disabled={disabled}
            onChange={() => setAnswers(answers.map((x, j) => ({ ...x, is_correct: j === i })))}
            inputProps={{ "aria-label": `Mark answer ${String.fromCharCode(65 + i)} correct` }}
          />
          <TextField
            size="small"
            fullWidth
            label={`Answer ${String.fromCharCode(65 + i)}`}
            value={a.answer_text}
            disabled={disabled}
            onChange={(e) => setAnswers(answers.map((x, j) => (j === i ? { ...x, answer_text: e.target.value } : x)))}
          />
        </Stack>
      ))}
      <Stack direction="row" spacing={0.75}>
        <TextField select size="small" label="Difficulty" value={difficulty} disabled={disabled} onChange={(e) => setDifficulty(e.target.value)} sx={{ width: 130 }}>
          <MenuItem value="easy">Easy</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="hard">Hard</MenuItem>
        </TextField>
      </Stack>
      <TextField size="small" label="Explanation" value={explanation} disabled={disabled} onChange={(e) => setExplanation(e.target.value)} fullWidth multiline minRows={2} />
      {problems.length ? (
        <Typography role="alert" sx={{ fontSize: 11.5, color: "#b45309" }}>{problems.join(" ")}</Typography>
      ) : null}
      <Stack direction="row" spacing={0.75}>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={disabled || problems.length > 0}
          onClick={() => onSave({ question_content: text, title: text.slice(0, 120), answers, difficulty, explanation })}
          sx={primaryButtonSx}
        >
          Save question
        </Button>
        <Button size="small" onClick={onCancel} sx={textButtonSx}>Cancel</Button>
      </Stack>
    </Stack>
  );
}

QuestionEditor.propTypes = {
  question: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/** AI-generated draft questions: review, edit or remove each before saving to the bank. */
export default function GeneratedQuestionsCard({ pending, actions, confirming, disabled }) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(null);
  const questions = pending?.questions || [];
  if (!questions.length) return null;
  const busy = disabled || confirming;
  const invalid = questions.filter((q) => questionProblems(q).length).length;

  return (
    <DraftCardShell
      kicker="DRAFT QUESTIONS"
      title={pending.learning_objective || "AI-generated questions"}
      summary={`${questions.length} question${questions.length === 1 ? "" : "s"} · saved to your private bank only after you approve`}
      icon={<FactCheckOutlinedIcon fontSize="small" />}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      footer={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Button size="small" variant="contained" disableElevation disabled={busy || invalid > 0} onClick={actions.save} sx={primaryButtonSx}>
            {confirming ? "Saving…" : `Save ${questions.length} to my bank`}
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={actions.discard} sx={outlineButtonSx}>
            Discard
          </Button>
          {invalid ? <Typography sx={{ fontSize: 11, color: "#b45309" }}>{invalid} need fixing</Typography> : null}
        </Stack>
      }
    >
      <Stack spacing={0.75} sx={{ maxHeight: 420, overflowY: "auto", pr: 0.25 }}>
        {questions.map((q, index) => {
          const correct = (q.answers || []).find((a) => a.is_correct)?.answer_text;
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{ p: 1, borderRadius: 2, border: "1px solid", borderColor: editing === index ? "rgba(0,97,254,0.45)" : "rgba(148,163,184,0.3)" }}
            >
              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <Typography sx={{ width: 18, flexShrink: 0, fontSize: 12, fontWeight: 800, color: AI_ASSISTANT_BLUE, pt: 0.2 }}>{index + 1}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, color: "#0f172a", fontWeight: 600 }}>
                    {q.question_content || q.title || "Untitled question"}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>
                    {q.difficulty || "medium"} · correct: {correct || "—"}
                    {q.question_image_url ? " · with figure" : ""}
                  </Typography>
                </Box>
                <IconButton size="small" aria-label={`Edit question ${index + 1}`} disabled={busy} onClick={() => setEditing(editing === index ? null : index)} sx={{ color: AI_ASSISTANT_BLUE }}>
                  <EditRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" aria-label={`Remove question ${index + 1}`} disabled={busy} onClick={() => actions.remove(index)} sx={{ color: "#b91c1c" }}>
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              {editing === index ? (
                <QuestionEditor
                  question={q}
                  disabled={busy}
                  onCancel={() => setEditing(null)}
                  onSave={(patch) => {
                    actions.update(index, patch);
                    setEditing(null);
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </DraftCardShell>
  );
}

GeneratedQuestionsCard.propTypes = {
  pending: PropTypes.object,
  actions: PropTypes.shape({
    save: PropTypes.func.isRequired,
    discard: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
  }).isRequired,
  confirming: PropTypes.bool,
  disabled: PropTypes.bool,
};
